/**
 * Product Hooks - Main Products Hook
 *
 * Unified interface for fetching products via the `get_client_products` RPC.
 *
 * Features:
 * - React Query caching (5-min stale time)
 * - Flexible filtering (featured, on sale, categories, price range, brands)
 * - Sorting (newest, popular, price high/low)
 * - Pagination support
 * - Type-safe with full TypeScript support
 * - Consistent error handling
 */

import { useQuery } from "@tanstack/react-query";
import type { UseProductsOptions, UseProductsResult, Product } from "./types";
import { transformRawProducts } from "./transformations";
import { productQueries, PRODUCT_QUERY_OPTIONS } from "./queryKeys";
import { getProducts } from "../../services/products.service";
import type { GetProductsPayload } from "../../services/products.service";

// ============================================================================
// SORT MAPPING
// ============================================================================

/**
 * Map hook's `sortType` + filter flags to the RPC's `sort_by` / `sort_order`.
 */
function mapSortAndFilters(
  sortType: UseProductsOptions["sortType"],
  filters?: UseProductsOptions["filters"],
): Pick<
  GetProductsPayload,
  "sort_by" | "sort_order" | "is_featured" | "is_on_sale"
> {
  const result: Pick<
    GetProductsPayload,
    "sort_by" | "sort_order" | "is_featured" | "is_on_sale"
  > = {};

  // Map sortType → sort_by + sort_order
  switch (sortType) {
    case "newest":
      result.sort_by = "created_at";
      result.sort_order = "desc";
      break;
    case "popular":
      result.sort_by = "popularity";
      result.sort_order = "desc";
      break;
    case "price_low_to_high":
      result.sort_by = "price";
      result.sort_order = "asc";
      break;
    case "price_high_to_low":
      result.sort_by = "price";
      result.sort_order = "desc";
      break;
    default:
      result.sort_by = "created_at";
      result.sort_order = "desc";
  }

  // Map filter flags
  if (filters?.isFeatured) result.is_featured = true;
  if (filters?.isOnSale) result.is_on_sale = true;
  if (filters?.storeFront) (result as any).store_front = true;

  return result;
}

// ============================================================================
// MAIN HOOK: useProducts
// ============================================================================

export function useProducts(
  options: UseProductsOptions = {},
): UseProductsResult {
  const {
    categoryId,
    sortType = "newest",
    page = 1,
    pageSize = 20,
    limit,
    offset,
    filters = {},
    enabled = true,
    staleTime,
    refetchOnWindowFocus = false,
  } = options;

  const finalLimit = limit || pageSize;
  const finalOffset = offset !== undefined ? offset : (page - 1) * finalLimit;

  // Build query key for caching
  const queryKey = productQueries.lists.filtered({
    categoryId,
    sortType,
    page,
    pageSize: finalLimit,
    filters,
  });

  // Query function
  const queryFn = async (): Promise<{
    products: Product[];
    totalCount: number;
  }> => {
    const sortAndFilters = mapSortAndFilters(sortType, filters);

    const payload: GetProductsPayload = {
      limit: finalLimit,
      offset: finalOffset,
      ...sortAndFilters,
    };

    // Category filter
    if (categoryId !== undefined && categoryId !== null) {
      payload.category_id = categoryId;
    }

    // Price range
    if (filters.priceRange) {
      if (filters.priceRange.min !== undefined)
        payload.min_price = filters.priceRange.min;
      if (filters.priceRange.max !== undefined)
        payload.max_price = filters.priceRange.max;
    }

    // Brand filter – take first brand id if provided
    if (filters.brands && filters.brands.length > 0) {
      const firstBrandId = parseInt(filters.brands[0], 10);
      if (!isNaN(firstBrandId)) payload.brand_id = firstBrandId;
    }

    // Search
    if (filters.searchQuery) {
      payload.search = filters.searchQuery;
    }

    const result = await getProducts(payload);

    // Transform raw data to standardized Product interface
    const products = transformRawProducts(result.products as any[], {
      sortType: sortType || "newest",
    });

    return { products, totalCount: result.totalCount };
  };

  const query = useQuery({
    queryKey,
    queryFn,
    enabled,
    staleTime: staleTime || PRODUCT_QUERY_OPTIONS.lists.staleTime,
    gcTime: PRODUCT_QUERY_OPTIONS.lists.gcTime,
    refetchOnWindowFocus,
    refetchOnMount: PRODUCT_QUERY_OPTIONS.lists.refetchOnMount,
    retry: PRODUCT_QUERY_OPTIONS.lists.retry,
  });

  return {
    products: query.data?.products || [],
    isLoading: query.isLoading,
    loading: query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
    isPending: query.isPending,
    isFetching: query.isFetching,
    refetch: query.refetch as () => void,
    totalCount: query.data?.totalCount || 0,
  };
}

// ============================================================================
// CONVENIENCE HOOKS (Thin Wrappers)
// ============================================================================

export function useFeaturedProducts(limit: number = 10): UseProductsResult {
  return useProducts({
    filters: { isFeatured: true },
    pageSize: limit,
    sortType: "popular",
  });
}

export function useOnSaleProducts(limit: number = 10): UseProductsResult {
  return useProducts({
    filters: { isOnSale: true },
    pageSize: limit,
    sortType: "popular",
  });
}

export function useRecentProducts(limit: number = 10): UseProductsResult {
  return useProducts({
    sortType: "newest",
    pageSize: limit,
  });
}

export function usePopularProducts(limit: number = 10): UseProductsResult {
  return useProducts({
    sortType: "popular",
    pageSize: limit,
  });
}

export function useCategoryProducts(
  categoryId: number,
  sortType: UseProductsOptions["sortType"] = "newest",
  limit: number = 20,
): UseProductsResult {
  return useProducts({
    categoryId,
    sortType,
    pageSize: limit,
  });
}

export function useSubCategoryProducts(
  _removedSubCategoryId: string,
  sortType: UseProductsOptions["sortType"] = "newest",
  limit: number = 20,
): UseProductsResult {
  console.warn(
    "[useSubCategoryProducts] Deprecated: subcategories removed from schema. Use useCategoryProducts instead.",
  );
  return useProducts({ sortType, pageSize: limit });
}

export function useBrandProducts(
  brandId: string,
  limit: number = 20,
): UseProductsResult {
  return useProducts({
    filters: { brands: [brandId] },
    sortType: "popular",
    pageSize: limit,
  });
}

// ============================================================================
// LEGACY COMPATIBILITY
// ============================================================================

/** @deprecated Use useProducts instead */
export function useProductsWithFilters(
  options: UseProductsOptions = {},
): UseProductsResult {
  console.warn("[useProductsWithFilters] Deprecated: Use useProducts instead");
  return useProducts(options);
}
